/**
 * JsonDiff: before/after viewer for audit evidence (§16.2 "append-only actor/action/before-after") and lottery
 * config versions (§5.3). `diffJson` is a pure flat diff; the view lists each changed path with text labels
 * (added / removed / changed) so colour is never the only signal.
 */
import { useMemo, useState } from 'react';

export type DiffKind = 'added' | 'removed' | 'changed';

export interface DiffEntry {
  path: string;
  kind: DiffKind;
  before?: unknown;
  after?: unknown;
}

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const joinPath = (base: string, key: string | number): string =>
  typeof key === 'number' ? `${base}[${key}]` : base ? `${base}.${key}` : key;

/** Flat diff of two JSON values; arrays are compared index by index. */
export function diffJson(before: unknown, after: unknown, base = ''): DiffEntry[] {
  if (isPlainObject(before) && isPlainObject(after)) {
    const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
    const out: DiffEntry[] = [];
    for (const key of [...keys].sort()) out.push(...diffJson(before[key], after[key], joinPath(base, key)));
    return out;
  }
  if (Array.isArray(before) && Array.isArray(after)) {
    const out: DiffEntry[] = [];
    const len = Math.max(before.length, after.length);
    for (let i = 0; i < len; i++) out.push(...diffJson(before[i], after[i], joinPath(base, i)));
    return out;
  }
  if (before === undefined && after !== undefined) return [{ path: base, kind: 'added', after }];
  if (before !== undefined && after === undefined) return [{ path: base, kind: 'removed', before }];
  if (JSON.stringify(before) !== JSON.stringify(after))
    return [{ path: base, kind: 'changed', before, after }];
  return [];
}

const show = (v: unknown): string => (v === undefined ? '—' : typeof v === 'string' ? v : JSON.stringify(v));

export interface JsonDiffProps {
  before: unknown;
  after: unknown;
  /** Labels for the two sides, e.g. "Version 3" / "Version 4". */
  beforeLabel?: string;
  afterLabel?: string;
  emptyText?: string;
}

export function JsonDiff({
  before,
  after,
  beforeLabel = 'Before',
  afterLabel = 'After',
  emptyText = 'No differences.',
}: JsonDiffProps) {
  const entries = useMemo(() => diffJson(before, after), [before, after]);
  const [showRaw, setShowRaw] = useState(false);
  return (
    <div className="wdc-diff">
      <div className="wdc-diff__toolbar">
        <p className="wd-muted">
          {entries.length} change{entries.length === 1 ? '' : 's'}
        </p>
        <button
          type="button"
          className="wd-btn"
          data-variant="quiet"
          aria-pressed={showRaw}
          onClick={() => setShowRaw((s) => !s)}
        >
          {showRaw ? 'Show changes' : 'Show raw JSON'}
        </button>
      </div>
      {showRaw ? (
        <div className="wdc-diff__raw">
          <div>
            <h4>{beforeLabel}</h4>
            <pre>{JSON.stringify(before, null, 2)}</pre>
          </div>
          <div>
            <h4>{afterLabel}</h4>
            <pre>{JSON.stringify(after, null, 2)}</pre>
          </div>
        </div>
      ) : entries.length === 0 ? (
        <p className="wd-muted">{emptyText}</p>
      ) : (
        <table className="wdc-table wdc-diff__table">
          <caption className="wd-sr-only">
            Changes from {beforeLabel} to {afterLabel}
          </caption>
          <thead>
            <tr>
              <th scope="col">Field</th>
              <th scope="col">Change</th>
              <th scope="col">{beforeLabel}</th>
              <th scope="col">{afterLabel}</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <tr key={e.path} data-kind={e.kind}>
                <th scope="row">
                  <code>{e.path}</code>
                </th>
                <td>
                  <span className="wdc-diff__kind" data-kind={e.kind}>
                    {e.kind}
                  </span>
                </td>
                <td>
                  <code>{show(e.before)}</code>
                </td>
                <td>
                  <code>{show(e.after)}</code>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
